/**
 * Hospital Capabilities Page
 * Customer-facing showcase of Sterile Mate's capabilities and AskIsaac platform
 * Designed for self-guided discovery or sales presentation accompaniment
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  Shield,
  Droplets,
  Sparkles,
  Package,
  Wrench,
  Award,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Phone,
  Mail,
  Clock,
  Lock,
  Smartphone,
  Database,
  Eye,
  FileText,
  Users,
  Zap,
  Activity,
  MapPin,
  BadgeCheck,
  FlaskConical,
  Microscope,
  CircleCheck,
  ArrowLeft,
  Play,
  Star,
  Monitor,
  BarChart3,
  Cpu,
  Bug,
  TestTube,
  ClipboardCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";

// Expandable Section Component
const CapabilitySection = ({ title, icon: Icon, color = "emerald", children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  const colorClasses = {
    emerald: "bg-emerald-600 border-emerald-500",
    blue: "bg-blue-600 border-blue-500",
    violet: "bg-violet-600 border-violet-500",
    amber: "bg-amber-600 border-amber-500",
    cyan: "bg-cyan-600 border-cyan-500",
    slate: "bg-slate-700 border-slate-600"
  };
  
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-6 py-4 flex items-center justify-between ${colorClasses[color]} text-white hover:opacity-95 transition-opacity`}
      >
        <div className="flex items-center gap-3">
          <Icon className="w-6 h-6" />
          <span className="text-lg font-bold">{title}</span>
        </div>
        {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
      </button>
      {isOpen && (
        <div className="p-6 bg-slate-50">
          {children}
        </div>
      )}
    </div>
  );
};

// Trust Badge Component
const TrustBadge = ({ icon: Icon, title, subtitle, color = "emerald" }) => {
  const colorClasses = {
    emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    violet: "bg-violet-100 text-violet-700 border-violet-200",
    amber: "bg-amber-100 text-amber-700 border-amber-200"
  };
  
  return (
    <div className={`${colorClasses[color]} rounded-xl p-4 border text-center`}>
      <Icon className="w-8 h-8 mx-auto mb-2" />
      <p className="font-bold text-sm">{title}</p>
      {subtitle && <p className="text-xs opacity-80">{subtitle}</p>}
    </div>
  );
};

export default function HospitalCapabilitiesPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/hospital/portal" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Dashboard</span>
            </Link>
            <div className="flex items-center gap-4">
              <a href="tel:8009883892" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white">
                <Phone className="w-4 h-4" />
                800.988.3892
              </a>
              <a href="mailto:george.broughton@sterilemate.com" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white">
                <Mail className="w-4 h-4" />
                Contact Us
              </a>
            </div>
          </div>
        </div>
        
        {/* Hero Section */}
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 border border-amber-500/30 rounded-full mb-6">
                <Award className="w-4 h-4 text-amber-400" />
                <span className="text-amber-300 text-sm font-semibold">5+ Years Serving the Texas Medical Center</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Your Partner in<br />
                <span className="text-cyan-400">Healthcare Operations</span>
              </h1>
              
              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                Sterile Mate LLC and AskIsaac.io — America&apos;s #1 source for back-ordered medical supplies, 
                water purification systems, and hospital-grade EVS solutions. Built in the trenches. 
                Trusted by hospitals nationwide.
              </p>
              
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-800 rounded-lg border border-slate-700">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-slate-300">ISO 17025 Accredited</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-800 rounded-lg border border-slate-700">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-slate-300">AAMI ST108 Compliant</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-800 rounded-lg border border-slate-700">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-slate-300">HIPAA Ready</span>
                </div>
              </div>
            </div>
            
            <div className="hidden lg:block">
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 border border-slate-700">
                <blockquote className="text-slate-300 text-lg italic mb-6 leading-relaxed">
                  &ldquo;Every conversation begins with one question: How can we help? 
                  Our mission is bigger than business. We&apos;re here to serve the people saving lives every day.&rdquo;
                </blockquote>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center">
                    <Users className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-white">George Broughton III</p>
                    <p className="text-slate-400 text-sm">Founder & CEO, Sterile Mate LLC</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Our Story Section */}
        <div className="mb-12">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-6 text-white">
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <Building2 className="w-7 h-7" />
                Our Story
              </h2>
              <p className="text-amber-100 mt-1">Born in crisis. Built for the frontline.</p>
            </div>
            <div className="p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-slate-800 mb-4">When the Supply Chain Broke, We Stepped In</h3>
                  <p className="text-slate-600 mb-4 leading-relaxed">
                    Sterile Mate was founded in September 2020 during the most challenging time our healthcare system 
                    had ever faced. When COVID-19 brought global supply chains to a halt, hospitals in the Texas Medical 
                    Center were scrambling for PPE, ventilator parts, and critical supplies.
                  </p>
                  <p className="text-slate-600 mb-4 leading-relaxed">
                    We became boots on the ground — sourcing back-ordered and hard-to-find items when primary suppliers 
                    couldn&apos;t deliver. We drove supplies directly to loading docks at 2 AM. We answered the phone when 
                    no one else would.
                  </p>
                  <p className="text-slate-800 font-medium">
                    Today, we&apos;re expanding to a new facility near the Texas Medical Center, placing us closer to the 
                    hospitals, clinics, and research institutions we proudly serve.
                  </p>
                </div>
                <div className="space-y-4">
                  <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                        <Package className="w-5 h-5 text-white" />
                      </div>
                      <h4 className="font-bold text-emerald-800">America&apos;s #1 Backorder Source</h4>
                    </div>
                    <p className="text-sm text-emerald-700">When Big Box distributors can&apos;t deliver, we find it fast from our network of verified suppliers.</p>
                  </div>
                  
                  <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                        <Droplets className="w-5 h-5 text-white" />
                      </div>
                      <h4 className="font-bold text-blue-800">Water Purification Experts</h4>
                    </div>
                    <p className="text-sm text-blue-700">AAMI ST108-compliant systems and monitoring software for patient safety through water quality excellence.</p>
                  </div>
                  
                  <div className="bg-violet-50 rounded-xl p-5 border border-violet-200">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-violet-600 rounded-lg flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-white" />
                      </div>
                      <h4 className="font-bold text-violet-800">EVS & Janitorial Division</h4>
                    </div>
                    <p className="text-sm text-violet-700">Hospital-grade cleaning solutions, microfiber systems, and autonomous floor care equipment.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Capabilities Sections */}
        <div className="space-y-6">
          {/* Hospital-Grade Security Suite */}
          <CapabilitySection title="Hospital-Grade Security Suite" icon={Shield} color="slate" defaultOpen={true}>
            <div className="space-y-6">
              <div className="bg-slate-900 rounded-xl p-6 text-white">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-emerald-400" />
                  Zero-Trust Security Architecture
                </h3>
                <p className="text-slate-300 mb-6">
                  AskIsaac operates on a Zero-Trust Architecture, treating every request as a potential threat 
                  until verified. Your facility infrastructure remains as secure as your clinical data.
                </p>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                    <div className="flex items-center gap-2 mb-2">
                      <Lock className="w-5 h-5 text-emerald-400" />
                      <span className="font-semibold">Zero-Knowledge Auth</span>
                    </div>
                    <p className="text-sm text-slate-400">Bcrypt Salting (Cost Factor 12). No plain-text passwords exist in the system.</p>
                  </div>
                  
                  <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                    <div className="flex items-center gap-2 mb-2">
                      <Smartphone className="w-5 h-5 text-emerald-400" />
                      <span className="font-semibold">Multi-Factor Authentication</span>
                    </div>
                    <p className="text-sm text-slate-400">TOTP-based MFA with Google Authenticator/Authy. Mandatory for Admin/Supplier roles.</p>
                  </div>
                  
                  <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-5 h-5 text-emerald-400" />
                      <span className="font-semibold">Session Timeout</span>
                    </div>
                    <p className="text-sm text-slate-400">30-minute inactivity auto-logout. Protects terminals in high-traffic clinical areas.</p>
                  </div>
                  
                  <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                    <div className="flex items-center gap-2 mb-2">
                      <Database className="w-5 h-5 text-emerald-400" />
                      <span className="font-semibold">WORM Audit Logs</span>
                    </div>
                    <p className="text-sm text-slate-400">Immutable Write-Once-Read-Many logs with SHA-256 integrity hashing. 100% audit trail.</p>
                  </div>
                  
                  <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                    <div className="flex items-center gap-2 mb-2">
                      <Eye className="w-5 h-5 text-emerald-400" />
                      <span className="font-semibold">Role-Based Access Control</span>
                    </div>
                    <p className="text-sm text-slate-400">Granular permissions: Hospital Admin, Facility Manager, Staff. Each sees only what they need.</p>
                  </div>
                  
                  <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-5 h-5 text-emerald-400" />
                      <span className="font-semibold">7-Year Secure Vault</span>
                    </div>
                    <p className="text-sm text-slate-400">All communications archived with 7-year retention. Joint Commission and regulatory audit-ready.</p>
                  </div>
                </div>
              </div>
              
              {/* Compliance Badges */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <TrustBadge icon={Shield} title="HIPAA Ready" subtitle="Data Protection" color="emerald" />
                <TrustBadge icon={Lock} title="SOC 2 Aligned" subtitle="Security Controls" color="blue" />
                <TrustBadge icon={Database} title="Encrypted Storage" subtitle="AES-256" color="violet" />
                <TrustBadge icon={Eye} title="Full Audit Trail" subtitle="100% Traceability" color="amber" />
              </div>
            </div>
          </CapabilitySection>

          {/* Water Purification Systems */}
          <CapabilitySection title="Healthcare Water Purification Systems" icon={Droplets} color="blue">
            <div className="space-y-6">
              {/* Partnership Banner */}
              <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl p-6 text-white">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="w-24 h-24 bg-white rounded-xl flex items-center justify-center p-4">
                    <Droplets className="w-12 h-12 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Water Purification Solutions</h3>
                    <p className="text-blue-100">
                      Complete water purification solutions for Sterile Processing, Dialysis, Laboratory, and Pharmaceutical applications. 
                      AAMI ST108 compliant systems with remote monitoring capabilities.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Product Categories */}
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <img 
                    src="https://sterilemate.com/wp-content/uploads/2025/11/RO-Systems-1.webp" 
                    alt="RO Systems"
                    className="w-full h-40 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-bold text-slate-800">RO/DI Systems</h4>
                    <p className="text-sm text-slate-600 mt-1">Reverse Osmosis and Deionization for critical water purity</p>
                    <ul className="mt-3 space-y-1 text-xs text-slate-500">
                      <li className="flex items-center gap-1"><CircleCheck className="w-3 h-3 text-emerald-500" /> AAMI ST108 Compliant</li>
                      <li className="flex items-center gap-1"><CircleCheck className="w-3 h-3 text-emerald-500" /> Remote Monitoring</li>
                      <li className="flex items-center gap-1"><CircleCheck className="w-3 h-3 text-emerald-500" /> Preventive Maintenance</li>
                    </ul>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <img 
                    src="https://sterilemate.com/wp-content/uploads/2025/11/Softner-Systems-1.webp" 
                    alt="Water Softeners"
                    className="w-full h-40 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-bold text-slate-800">Water Softeners</h4>
                    <p className="text-sm text-slate-600 mt-1">Hardness removal for equipment protection</p>
                    <ul className="mt-3 space-y-1 text-xs text-slate-500">
                      <li className="flex items-center gap-1"><CircleCheck className="w-3 h-3 text-emerald-500" /> Scale Prevention</li>
                      <li className="flex items-center gap-1"><CircleCheck className="w-3 h-3 text-emerald-500" /> Extends Equipment Life</li>
                      <li className="flex items-center gap-1"><CircleCheck className="w-3 h-3 text-emerald-500" /> Automated Regeneration</li>
                    </ul>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <img 
                    src="https://sterilemate.com/wp-content/uploads/2025/11/Water-Monitoring-1.webp" 
                    alt="Water Monitoring"
                    className="w-full h-40 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-bold text-slate-800">Monitoring & Testing</h4>
                    <p className="text-sm text-slate-600 mt-1">Real-time quality monitoring and lab testing</p>
                    <ul className="mt-3 space-y-1 text-xs text-slate-500">
                      <li className="flex items-center gap-1"><CircleCheck className="w-3 h-3 text-emerald-500" /> ISO 17025 Accredited Lab</li>
                      <li className="flex items-center gap-1"><CircleCheck className="w-3 h-3 text-emerald-500" /> 24/7 Remote Monitoring</li>
                      <li className="flex items-center gap-1"><CircleCheck className="w-3 h-3 text-emerald-500" /> Compliance Reporting</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Lab Accreditations */}
              <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
                <h4 className="font-bold text-emerald-800 mb-4 flex items-center gap-2">
                  <Microscope className="w-5 h-5" />
                  Microbiology Testing & Water Sampling (ISO/IEC 17025:2017 Accredited)
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-3 text-center border border-emerald-200">
                    <p className="font-bold text-emerald-700">ISO/IEC 17025</p>
                    <p className="text-xs text-slate-500">International Standard</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-emerald-200">
                    <p className="font-bold text-emerald-700">TCEQ Approved</p>
                    <p className="text-xs text-slate-500">Drinking Water Lab</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-emerald-200">
                    <p className="font-bold text-emerald-700">NELAP Accredited</p>
                    <p className="text-xs text-slate-500">Environmental Testing</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-emerald-200">
                    <p className="font-bold text-emerald-700">CDC Legionella Elite</p>
                    <p className="text-xs text-slate-500">Specialized Program</p>
                  </div>
                </div>
              </div>
            </div>
          </CapabilitySection>

          {/* Janitorial & EVS */}
          <CapabilitySection title="Janitorial & EVS Capabilities" icon={Sparkles} color="emerald">
            <div className="space-y-6">
              {/* Hero Banner */}
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
                <h3 className="text-xl font-bold mb-2">Hospital-Grade EVS Solutions</h3>
                <p className="text-emerald-100">
                  5+ years supplying janitorial disposables, microfiber products, and hardware solutions to the Texas Medical Center. 
                  Authorized distributor for industry-leading brands.
                </p>
              </div>
              
              {/* Distributor Badges */}
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <h4 className="font-bold text-slate-800 mb-4 text-center flex items-center justify-center gap-2">
                  <BadgeCheck className="w-5 h-5 text-emerald-600" />
                  Authorized Distributor for Industry-Leading Brands
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {[
                    { name: "Filmop USA", specialty: "Workstations & Microfiber" },
                    { name: "MultiClean", specialty: "Cleaning Chemicals" },
                    { name: "Minuteman", specialty: "Floor Equipment" },
                    { name: "Microfiber USA", specialty: "Microfiber Products" },
                    { name: "ABCO Products", specialty: "Mops & Brooms" }
                  ].map((brand, i) => (
                    <div key={i} className="bg-white rounded-lg p-4 text-center border border-slate-200">
                      <p className="font-bold text-slate-800">{brand.name}</p>
                      <p className="text-xs text-emerald-600">{brand.specialty}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Product Categories */}
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-emerald-600 p-4 text-white">
                    <h5 className="font-bold">Janitorial Disposables</h5>
                  </div>
                  <div className="p-4 space-y-2">
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-emerald-500" /> Trash Liners (All Sizes)
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-emerald-500" /> Gloves (Nitrile, Latex, Vinyl)
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-emerald-500" /> Paper Products
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-emerald-500" /> Disinfectant Wipes
                    </p>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-blue-600 p-4 text-white">
                    <h5 className="font-bold">Microfiber Systems</h5>
                  </div>
                  <div className="p-4 space-y-2">
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-blue-500" /> Flat Mop Pads (Color-Coded)
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-blue-500" /> Microfiber Cloths
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-blue-500" /> Dust & String Mops
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-blue-500" /> Green Seal Certified
                    </p>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-violet-600 p-4 text-white">
                    <h5 className="font-bold">Hardware Solutions</h5>
                  </div>
                  <div className="p-4 space-y-2">
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-violet-500" /> Walk-Behind Scrubbers
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-violet-500" /> Autonomous Floor Care
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-violet-500" /> Critical Filter Vacuums
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-violet-500" /> EVS Workstations
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Product Images */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <img src="https://sterilemate.com/wp-content/uploads/2025/11/Autonomous-Floor-Care.webp" alt="Autonomous Floor Care" className="w-full h-32 object-cover" />
                  <div className="p-3">
                    <p className="font-semibold text-sm text-slate-800">Autonomous Floor Care</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <img src="https://www.filmopusa.com/images/product-page-images/workstation-overview-top-950.gif" alt="Filmop Workstations" className="w-full h-32 object-cover" />
                  <div className="p-3">
                    <p className="font-semibold text-sm text-slate-800">Alpha Workstations</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <img src="https://sterilemate.com/wp-content/uploads/2025/11/Hospital-Vacuum-1.webp" alt="Critical Filter Vacuums" className="w-full h-32 object-cover" />
                  <div className="p-3">
                    <p className="font-semibold text-sm text-slate-800">HEPA Vacuums</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <img src="https://sterilemate.com/wp-content/uploads/2025/11/EZ-Mop-360-Microscrubber-1.webp" alt="Floor Scrubbers" className="w-full h-32 object-cover" />
                  <div className="p-3">
                    <p className="font-semibold text-sm text-slate-800">Floor Scrubbers</p>
                  </div>
                </div>
              </div>
              
              {/* Shop Filmop USA Marketplace CTA */}
              <Link to="/hospital/marketplace" className="block">
                <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 rounded-xl p-6 text-white hover:shadow-lg transition-shadow cursor-pointer">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                        <Package className="w-8 h-8" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-1">Shop Filmop USA Products</h3>
                        <p className="text-emerald-100">
                          Browse our full catalog of 700+ professional cleaning products. 
                          2-day delivery in Texas, 3-5 days nationwide.
                        </p>
                      </div>
                    </div>
                    <div className="hidden md:flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg">
                      <span className="font-semibold">Visit Marketplace</span>
                      <ChevronRight className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </CapabilitySection>

          {/* Backorder Solutions */}
          <CapabilitySection title="Backorder Solutions" icon={Package} color="amber">
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-6 text-white">
                <h3 className="text-xl font-bold mb-2">America&apos;s #1 Source for Back-Ordered Medical Supplies</h3>
                <p className="text-amber-100">
                  When your primary distributor can&apos;t deliver, we mobilize our network to find what you need — fast. 
                  Verified suppliers, competitive pricing, and transparent tracking through the AskIsaac platform.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <h4 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-amber-500" />
                    How It Works
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-bold text-sm flex-shrink-0">1</div>
                      <div>
                        <p className="font-semibold text-slate-800">Submit Your Request</p>
                        <p className="text-sm text-slate-600">Enter backorder details through AskIsaac or contact us directly</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-bold text-sm flex-shrink-0">2</div>
                      <div>
                        <p className="font-semibold text-slate-800">We Source It</p>
                        <p className="text-sm text-slate-600">Our network of verified suppliers competes to fulfill your request</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-bold text-sm flex-shrink-0">3</div>
                      <div>
                        <p className="font-semibold text-slate-800">Compare & Approve</p>
                        <p className="text-sm text-slate-600">Review quotes, select the best option, and approve with one click</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 font-bold text-sm flex-shrink-0">✓</div>
                      <div>
                        <p className="font-semibold text-slate-800">Track & Receive</p>
                        <p className="text-sm text-slate-600">Real-time tracking until delivery. Full audit trail for compliance.</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-slate-900 rounded-xl p-6 text-white">
                  <h4 className="font-bold mb-4 flex items-center gap-2">
                    <Star className="w-5 h-5 text-amber-400" />
                    Why Hospitals Trust Us
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <p className="text-slate-300">5+ years serving the Texas Medical Center</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <p className="text-slate-300">Verified supplier network with quality guarantees</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <p className="text-slate-300">Competitive bidding drives better pricing</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <p className="text-slate-300">Complete documentation for compliance</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <p className="text-slate-300">Local presence — we deliver to your dock</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CapabilitySection>

          {/* Contractor Network */}
          <CapabilitySection title="Pre-Vetted Contractor Network" icon={Wrench} color="cyan">
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-xl p-6 text-white">
                <h3 className="text-xl font-bold mb-2">On-Demand Facility Support Through AskIsaac</h3>
                <p className="text-cyan-100">
                  Pre-vetted local contractors for plumbing, electrical, HVAC, and general repairs. 
                  GPS-verified arrival, photo documentation, and automatic compliance reporting.
                </p>
              </div>
              
              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-5 border border-slate-200 text-center">
                  <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Wrench className="w-6 h-6 text-cyan-600" />
                  </div>
                  <p className="font-bold text-slate-800">Plumbing</p>
                  <p className="text-xs text-slate-500 mt-1">Emergency & scheduled</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-slate-200 text-center">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Zap className="w-6 h-6 text-amber-600" />
                  </div>
                  <p className="font-bold text-slate-800">Electrical</p>
                  <p className="text-xs text-slate-500 mt-1">Licensed & insured</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-slate-200 text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Activity className="w-6 h-6 text-blue-600" />
                  </div>
                  <p className="font-bold text-slate-800">HVAC</p>
                  <p className="text-xs text-slate-500 mt-1">24/7 availability</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-slate-200 text-center">
                  <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Building2 className="w-6 h-6 text-violet-600" />
                  </div>
                  <p className="font-bold text-slate-800">General Repairs</p>
                  <p className="text-xs text-slate-500 mt-1">Multi-trade support</p>
                </div>
              </div>
              
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <h4 className="font-bold text-slate-800 mb-4">Every Service Call Includes:</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-cyan-600" />
                    <span className="text-slate-700">GPS-Verified Arrival & Departure</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-cyan-600" />
                    <span className="text-slate-700">Photo Documentation</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-cyan-600" />
                    <span className="text-slate-700">Compliance Audit Trail</span>
                  </div>
                </div>
              </div>
            </div>
          </CapabilitySection>

          {/* Microbiology Testing & Infection Prevention */}
          <CapabilitySection title="Microbiology Testing & Infection Prevention Validation" icon={Microscope} color="emerald">
            <div className="space-y-6">
              {/* Partnership Banner */}
              <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-xl p-6 text-white">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="w-24 h-24 bg-white rounded-xl flex items-center justify-center p-4">
                    <TestTube className="w-12 h-12 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Sterile Mate × A&B Labs Partnership</h3>
                    <p className="text-teal-100">
                      ISO 17025 accredited laboratory services for comprehensive environmental and microbiology testing. 
                      Featuring same-day Legionella PCR testing — in by 9am, results by 5pm.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Key Services */}
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-red-600 p-4 text-white">
                    <h5 className="font-bold flex items-center gap-2">
                      <Bug className="w-5 h-5" />
                      Legionella Testing
                    </h5>
                  </div>
                  <div className="p-4 space-y-2">
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-emerald-500" /> Same-Day PCR Results
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-emerald-500" /> CDC ELITE Certified
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-emerald-500" /> Serogroup 1 & 2-14 Testing
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-emerald-500" /> Water Management Plans
                    </p>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-blue-600 p-4 text-white">
                    <h5 className="font-bold flex items-center gap-2">
                      <Droplets className="w-5 h-5" />
                      Environmental Testing
                    </h5>
                  </div>
                  <div className="p-4 space-y-2">
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-blue-500" /> Drinking Water Microbiology
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-blue-500" /> Total Coliforms & E.coli
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-blue-500" /> Heterotrophic Plate Count
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-blue-500" /> Pseudomonas Testing
                    </p>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-violet-600 p-4 text-white">
                    <h5 className="font-bold flex items-center gap-2">
                      <ClipboardCheck className="w-5 h-5" />
                      Infection Prevention
                    </h5>
                  </div>
                  <div className="p-4 space-y-2">
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-violet-500" /> Surface Validation Testing
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-violet-500" /> ATP Monitoring
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-violet-500" /> Sterile Processing Validation
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-violet-500" /> Chain of Custody Documentation
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Accreditations */}
              <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
                <h4 className="font-bold text-emerald-800 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  Laboratory Accreditations & Certifications
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  <div className="bg-white rounded-lg p-3 text-center border border-emerald-200">
                    <p className="font-bold text-emerald-700 text-sm">ISO 17025</p>
                    <p className="text-xs text-slate-500">2017</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-emerald-200">
                    <p className="font-bold text-emerald-700 text-sm">NELAP</p>
                    <p className="text-xs text-slate-500">Accredited</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-emerald-200">
                    <p className="font-bold text-emerald-700 text-sm">TCEQ</p>
                    <p className="text-xs text-slate-500">Approved</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-emerald-200">
                    <p className="font-bold text-emerald-700 text-sm">CDC ELITE</p>
                    <p className="text-xs text-slate-500">Legionella</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-emerald-200">
                    <p className="font-bold text-emerald-700 text-sm">AIHA LAP</p>
                    <p className="text-xs text-slate-500">Member</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-emerald-200">
                    <p className="font-bold text-emerald-700 text-sm">NVLAP</p>
                    <p className="text-xs text-slate-500">Certified</p>
                  </div>
                </div>
              </div>
              
              {/* CTA */}
              <Link to="/hospital/portal?tab=water-testing" className="block">
                <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-xl p-6 text-white hover:shadow-lg transition-shadow cursor-pointer">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                        <FlaskConical className="w-8 h-8" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-1">Schedule Microbiology Testing</h3>
                        <p className="text-teal-100">
                          Order tests, build recurring sample schedules, and track results through your Hospital Portal.
                        </p>
                      </div>
                    </div>
                    <div className="hidden md:flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg">
                      <span className="font-semibold">Get Started</span>
                      <ChevronRight className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </CapabilitySection>

          {/* Supply Chain Software Solutions - IMS HART */}
          <CapabilitySection title="Supply Chain Software Solutions — IMS HART" icon={Monitor} color="blue">
            <div className="space-y-6">
              {/* Hero Banner */}
              <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 rounded-xl p-6 text-white">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="w-24 h-24 bg-white rounded-xl flex items-center justify-center p-3">
                    <div className="text-center">
                      <p className="font-black text-2xl text-blue-600">IMS</p>
                      <p className="font-bold text-lg text-indigo-600">HART</p>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Healthcare Asset & Resource Tracking</h3>
                    <p className="text-blue-100">
                      Revolutionize how your facility manages implants, prosthetics, and high-value medical devices. 
                      Real-time tracking, automated billing integration, and complete chain of custody documentation.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Key Features */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-5 border border-slate-200">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                    <BarChart3 className="w-6 h-6 text-blue-600" />
                  </div>
                  <h5 className="font-bold text-slate-800 mb-2">Implant Tracking</h5>
                  <p className="text-sm text-slate-600">Real-time visibility into implant inventory, usage, and expiration across all facilities.</p>
                </div>
                
                <div className="bg-white rounded-xl p-5 border border-slate-200">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                    <Database className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h5 className="font-bold text-slate-800 mb-2">Automated Billing</h5>
                  <p className="text-sm text-slate-600">Seamless integration with your billing systems. Capture every chargeable item automatically.</p>
                </div>
                
                <div className="bg-white rounded-xl p-5 border border-slate-200">
                  <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center mb-3">
                    <Cpu className="w-6 h-6 text-violet-600" />
                  </div>
                  <h5 className="font-bold text-slate-800 mb-2">Smart Analytics</h5>
                  <p className="text-sm text-slate-600">AI-powered insights for demand forecasting, par level optimization, and cost reduction.</p>
                </div>
                
                <div className="bg-white rounded-xl p-5 border border-slate-200">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-3">
                    <Shield className="w-6 h-6 text-amber-600" />
                  </div>
                  <h5 className="font-bold text-slate-800 mb-2">Compliance Ready</h5>
                  <p className="text-sm text-slate-600">Complete audit trails, recall management, and regulatory compliance documentation.</p>
                </div>
              </div>
              
              {/* Benefits */}
              <div className="bg-slate-900 rounded-xl p-6 text-white">
                <h4 className="font-bold mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-400" />
                  Why Healthcare Systems Choose IMS HART
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <p className="text-slate-300">Reduce supply costs by 15-25%</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <p className="text-slate-300">Eliminate expired inventory waste</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <p className="text-slate-300">Capture 100% of billable implants</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <p className="text-slate-300">Real-time recall management</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <p className="text-slate-300">Streamline Joint Commission audits</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <p className="text-slate-300">Cloud-based, HIPAA compliant</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Demo CTA */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                      <Play className="w-8 h-8" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-1">See IMS HART in Action</h3>
                      <p className="text-blue-100">
                        Watch our demo video to see how IMS HART transforms implant management and supply chain operations.
                      </p>
                    </div>
                  </div>
                  <a 
                    href="https://www.youtube.com/watch?v=your-hart-demo"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-semibold whitespace-nowrap"
                  >
                    <Play className="w-5 h-5" />
                    Watch Demo
                  </a>
                </div>
              </div>
            </div>
          </CapabilitySection>
        </div>

        {/* Contact CTA */}
        <div className="mt-12 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-8 text-white text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to Partner With Us?</h2>
          <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
            Whether you need back-ordered supplies, water purification systems, EVS solutions, or on-demand facility support — 
            we&apos;re here to help. Contact us for a consultation or demo.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="tel:8009883892"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-900 rounded-lg hover:bg-slate-100 transition-colors font-medium"
            >
              <Phone className="w-5 h-5" />
              800.988.3892
            </a>
            <a 
              href="mailto:george.broughton@sterilemate.com"
              className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-medium"
            >
              <Mail className="w-5 h-5" />
              Contact George Broughton
            </a>
            <a 
              href="https://sterilemate.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-700 text-white border border-slate-600 rounded-lg hover:bg-slate-600 transition-colors font-medium"
            >
              <ExternalLink className="w-5 h-5" />
              Visit SterileMate.com
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} Sterile Mate LLC / AskIsaac Inc. All rights reserved.
          </p>
          <p className="text-slate-500 text-xs mt-2">
            Houston, Texas | Serving the Texas Medical Center and Healthcare Facilities Nationwide
          </p>
        </div>
      </footer>
    </div>
  );
}
